import { createClient } from '@supabase/supabase-js'
import { emitDataChanged } from './utils/dataBus'

const supabaseUrl = 'https://nhizilvpewytxunameui.supabase.co'
const supabaseKey = 'sb_publishable_vHR9ytLxodgcul95pbq8uQ__rX6P29X'

const client = createClient(supabaseUrl, supabaseKey, {
  auth: {
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
  },
})

// ---- Candado de solo-lectura por suscripción vencida ----
// Cuando la suscripción del estudio quedó vencida (pasado el período de
// gracia), AppShell prende este flag. Con él activo, cualquier escritura de
// CONTENIDO (insert/update/upsert/delete y los RPC de borrado) se corta acá
// mismo, antes de tocar la red, sin importar la pantalla. Se avisa por
// onBloqueo para mostrar un aviso al usuario. Las tablas de "plomería" de la
// cuenta quedan exentas para no romper el login ni el propio flujo de pago.
//
// Nota honesta: esto es un candado de front-end. Frena la app real, pero un
// usuario técnico podría escribir igual llamando a la API directo (la RLS no
// chequea estado de suscripción). Para un bloqueo infranqueable haría falta
// enforcement en la base (RLS/trigger según estado de suscripción) — queda
// como paso siguiente si hace falta.
let modoSoloLectura = false;
let onBloqueo = null;
export function setModoSoloLectura(v) { modoSoloLectura = !!v; }
export function setOnBloqueo(fn) { onBloqueo = fn; }

const TABLAS_EXENTAS = new Set(['profiles', 'estudio_miembros', 'estudios', 'suscripciones']);
const RPC_BLOQUEABLES = new Set(['eliminar_tramite', 'eliminar_ingreso', 'eliminar_deuda']);

function respuestaBloqueada() {
  if (onBloqueo) { try { onBloqueo(); } catch { /* noop */ } }
  return {
    data: null,
    error: {
      message: 'Tu suscripción venció. Activá el pago para volver a crear o editar.',
      code: 'SUSCRIPCION_VENCIDA',
    },
  };
}

// Intercepta cualquier escritura (insert/update/upsert/delete) sobre
// cualquier tabla y avisa automáticamente por dataBus, sin que el código
// que escribe tenga que acordarse de emitir nada. Así cualquier vista que
// muestre esos datos puede suscribirse y refrescarse sola — incluida la
// funcionalidad que se agregue de acá en adelante.
const WRITE_METHODS = ['insert', 'update', 'upsert', 'delete'];
const originalFrom = client.from.bind(client);

client.from = function (table) {
  const builder = originalFrom(table);
  WRITE_METHODS.forEach((method) => {
    const original = builder[method]?.bind(builder);
    if (!original) return;
    builder[method] = (...args) => {
      const result = original(...args);
      const originalThen = result.then.bind(result);
      result.then = (onFulfilled, onRejected) => {
        if (modoSoloLectura && !TABLAS_EXENTAS.has(table)) {
          const res = respuestaBloqueada();
          return Promise.resolve(onFulfilled ? onFulfilled(res) : res);
        }
        return originalThen((res) => {
          if (!res?.error) emitDataChanged(table, method, res?.data);
          return onFulfilled ? onFulfilled(res) : res;
        }, onRejected);
      };
      return result;
    };
  });
  return builder;
};

// Las escrituras que pasan por una función RPC (SECURITY DEFINER, para
// bypassear RLS — ej. eliminar_tramite, eliminar_ingreso) no pasan por
// client.from() y por lo tanto no emitían nada. Mapa explícito de qué
// tablas toca cada RPC de escritura: al agregar una RPC nueva que
// modifique datos, sumarla acá es lo único que hace falta para que las
// vistas que dependen de esas tablas se refresquen solas.
const RPC_TABLES = {
  eliminar_tramite: ['tramites', 'tareas', 'agenda', 'tramites_notas'],
  eliminar_ingreso: ['ingresos', 'deudas'],
  eliminar_deuda: ['deudas'],
};

const originalRpc = client.rpc.bind(client);
client.rpc = function (fn, args, options) {
  if (modoSoloLectura && RPC_BLOQUEABLES.has(fn)) {
    return Promise.resolve(respuestaBloqueada());
  }
  const result = originalRpc(fn, args, options);
  const tables = RPC_TABLES[fn];
  if (!tables) return result;
  const originalThen = result.then.bind(result);
  result.then = (onFulfilled, onRejected) =>
    originalThen((res) => {
      if (!res?.error) tables.forEach(t => emitDataChanged(t, 'rpc:' + fn, res?.data));
      return onFulfilled ? onFulfilled(res) : res;
    }, onRejected);
  return result;
};

export const supabase = client;